import { Download, FileText, FolderOpen, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api, fetchFile } from '../lib/api';
import { fileSize, prepareSample, type SampleUpload } from '../lib/samples';
import type { DocumentKind, HomeschoolDocument } from '../lib/types';
import { Section } from '../ui/Collapsible';
import { useParent } from './ParentContext';

/**
 * Homeschool documents (#624, part of #453), in Settings → School Settings: the family's filed paperwork in one place,
 * by school year. Uploads, state documents saved from a deadline, and each year's portfolio when the year is closed out.
 */

export const DOCUMENT_KINDS: { id: DocumentKind; label: string }[] = [
  { id: 'notice', label: 'Notice of intent' },
  { id: 'acceptance', label: 'Acceptance letter' },
  { id: 'ihip', label: 'Instruction plan (IHIP)' },
  { id: 'report', label: 'Quarterly or progress report' },
  { id: 'evaluation', label: 'Evaluation report' },
  { id: 'test', label: 'Test report' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'transcript', label: 'Transcript' },
  { id: 'diploma', label: 'Diploma' },
  { id: 'other', label: 'Other' },
];
const kindLabel = (k: DocumentKind) => DOCUMENT_KINDS.find((x) => x.id === k)?.label ?? 'Other';
const SOURCE: Record<HomeschoolDocument['source'], string> = { upload: 'Uploaded', made: 'Made in ClassGuardian', closeout: 'Closed out the year' };
const fmt = (ymd: string) => new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const today = () => new Date().toLocaleDateString('en-CA');
/** School years run July to June: "2025–26". */
const yearOf = (ymd: string) => {
  const y = Number(ymd.slice(0, 4)) - (Number(ymd.slice(5, 7)) >= 7 ? 0 : 1);
  return `${y}–${String(y + 1).slice(2)}`;
};

interface Data {
  enabled: boolean;
  maxMb: number;
  storage: { usedBytes: number; limitMb: number };
  documents: HomeschoolDocument[];
}
interface Draft {
  title: string;
  kind: DocumentKind;
  date: string;
  childId: string;
  file: SampleUpload | null;
}

export function DocumentsSettings({ embedded = false }: { embedded?: boolean }) {
  const { toast, children } = useParent();
  const [data, setData] = useState<Data | null>(null);
  const [who, setWho] = useState('all');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api
      .get<Data>('/api/documents')
      .then(setData)
      .catch(() => setData(null));
  }, []);
  useEffect(load, [load]);
  if (!data?.enabled) return null;

  const nameOf = (id: string | null) => (id ? (children.find((c) => c.id === id)?.name ?? 'A child') : 'Whole family');
  const shown = data.documents.filter((d) => who === 'all' || !d.childId || d.childId === who);
  const years = [...new Set(shown.map((d) => yearOf(d.date)))];
  const set = (patch: Partial<Draft>) => draft && setDraft({ ...draft, ...patch });
  const pct = Math.min(100, Math.round((data.storage.usedBytes / (data.storage.limitMb * 1024 * 1024)) * 100));

  async function save() {
    if (!draft?.file) return;
    setBusy(true);
    try {
      await api.post('/api/documents', { title: draft.title, kind: draft.kind, date: draft.date, childId: draft.childId, file: { name: draft.file.name, data: draft.file.data } });
      setDraft(null);
      load();
      toast({ text: 'Saved to Homeschool documents.', tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }
  async function remove(d: HomeschoolDocument) {
    if (!confirm(`Remove “${d.title}”? This can’t be undone.`)) return;
    try {
      await api.del(`/api/documents/${d.id}`);
      load();
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }
  async function download(d: HomeschoolDocument) {
    try {
      const url = URL.createObjectURL(await fetchFile(`/api/documents/${d.id}/file`));
      const a = document.createElement('a');
      a.href = url;
      a.download = d.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <Section embedded={embedded} id="documents" title="Homeschool documents" icon={<FolderOpen size={18} />} summary={`${data.documents.length} document${data.documents.length === 1 ? '' : 's'}`}>
      <div className="stack documents">
        <div className="row-between wrap">
          <p className="muted small grow">
            Filed paperwork in one place: notices of intent, acceptance letters, evaluator and test reports, diplomas. Closing out a school year saves its portfolios here.
          </p>
          <button className="btn small" onClick={() => setDraft(draft ? null : { title: '', kind: 'acceptance', date: today(), childId: '', file: null })}>
            {draft ? 'Cancel' : (
              <>
                <Plus size={15} /> Add a document
              </>
            )}
          </button>
        </div>
        {draft && (
          <div className="test-form stack">
            <div className="field-row wrap-row">
              <label className="grow">
                Title
                <input value={draft.title} onChange={(e) => set({ title: e.target.value })} maxLength={160} placeholder="e.g. Superintendent acceptance letter" />
              </label>
              <label>
                Kind
                <select value={draft.kind} onChange={(e) => set({ kind: e.target.value as DocumentKind })}>
                  {DOCUMENT_KINDS.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Date
                <input type="date" value={draft.date} onChange={(e) => set({ date: e.target.value })} />
              </label>
              <label>
                For
                <select value={draft.childId} onChange={(e) => set({ childId: e.target.value })}>
                  <option value="">Whole family</option>
                  {children.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="row-between wrap">
              <label className="btn small file-pick">
                <FileText size={15} /> {draft.file ? `${draft.file.name} · ${fileSize(draft.file.size)}` : 'Choose the file (PDF or photo)'}
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (!f) return;
                    try {
                      const ready = await prepareSample(f, data.maxMb);
                      set({ file: ready, title: draft.title || f.name.replace(/\.[a-z0-9]+$/i, '') });
                    } catch (err) {
                      toast({ text: (err as Error).message, tone: 'alert' });
                    }
                  }}
                />
              </label>
              <button className="btn primary" disabled={busy || !draft.file || !draft.title.trim() || !draft.date} onClick={save}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
        {children.length > 1 && data.documents.length > 0 && (
          <div className="row-gap wrap" role="group" aria-label="Show documents for">
            <span className="muted small">Show</span>
            {[{ id: 'all', name: 'Everyone' }, ...children].map((c) => (
              <button key={c.id} type="button" className={`chip ${who === c.id ? 'active' : ''}`} aria-pressed={who === c.id} onClick={() => setWho(c.id)}>
                {c.name}
              </button>
            ))}
          </div>
        )}
        {!data.documents.length && !draft && <p className="muted">No documents yet.</p>}
        {years.map((y) => (
          <div key={y} className="stack doc-year">
            <span className="doc-year-label">{y} school year</span>
            {shown
              .filter((d) => yearOf(d.date) === y)
              .map((d) => (
                <div key={d.id} className="doc-row">
                  <span className={`doc-icon ${d.source}`} aria-hidden>
                    {d.mime === 'application/pdf' ? 'PDF' : 'IMG'}
                  </span>
                  <div className="stack tight doc-text">
                    <strong>{d.title}</strong>
                    <span className="muted small">{[kindLabel(d.kind), fmt(d.date), nameOf(d.childId), fileSize(d.size)].join(' · ')}</span>
                    <span className="doc-source small">{SOURCE[d.source]}</span>
                  </div>
                  <button className="icon-btn" onClick={() => download(d)} aria-label={`Download ${d.title}`} title="Download">
                    <Download size={16} />
                  </button>
                  <button className="icon-btn" aria-label={`Remove ${d.title}`} onClick={() => remove(d)}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
          </div>
        ))}
        <div className="stack tight">
          <div className="storage-bar" aria-hidden>
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="muted small">
            Documents and work samples use {fileSize(data.storage.usedBytes)} of your family’s {data.storage.limitMb} MB.
          </span>
        </div>
      </div>
    </Section>
  );
}
